import { useRef, useMemo, useCallback, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import BlotFormatter from "@enzedonline/quill-blot-formatter2";
import "@enzedonline/quill-blot-formatter2/dist/css/quill-blot-formatter2.css";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { uploadAnyFile } from "@/lib/uploads";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowUp, ArrowDown, X, Plus, Loader2 } from "lucide-react";

// Riferimenti a livello modulo per far dialogare i blot custom con il componente
let __activeQuill = null;
let __onGalleryEdit = null;

// Registrazione una tantum (resize + allineamento immagini per Quill 2)
if (!Quill.__blotFormatterRegistered) {
  BlotFormatter.registerFormats(Quill);
  Quill.register("modules/blotFormatter2", BlotFormatter);
  Quill.__blotFormatterRegistered = true;
}

// Blot personalizzati: video file (mp4) inline + galleria foto scorrevole
if (!Quill.__mediaBlotsRegistered) {
  const BlockEmbed = Quill.import("blots/block/embed");

  class VideoFileBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      const src = typeof value === "string" ? value : value?.src || "";
      node.setAttribute("src", src);
      node.setAttribute("controls", "");
      node.setAttribute("controlslist", "nodownload");
      node.setAttribute("playsinline", "");
      node.classList.add("ql-article-video");
      return node;
    }
    static value(node) { return node.getAttribute("src"); }
  }
  VideoFileBlot.blotName = "videofile";
  VideoFileBlot.tagName = "video";
  VideoFileBlot.className = "ql-article-video";
  Quill.register(VideoFileBlot);

  class GalleryBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      node.classList.add("article-gallery");
      const urls = Array.isArray(value) ? value : String(value || "").split("|").filter(Boolean);
      node.setAttribute("data-images", urls.join("|"));
      node.setAttribute("contenteditable", "false");
      node.setAttribute("title", "Clicca per gestire la galleria (riordina/rimuovi foto)");
      urls.forEach((u) => { const img = document.createElement("img"); img.setAttribute("src", u); node.appendChild(img); });
      node.addEventListener("click", (e) => {
        e.preventDefault(); e.stopPropagation();
        if (__onGalleryEdit) __onGalleryEdit(node);
      });
      return node;
    }
    static value(node) {
      const d = node.getAttribute("data-images");
      if (d) return d.split("|").filter(Boolean);
      return Array.from(node.querySelectorAll("img")).map((i) => i.getAttribute("src"));
    }
  }
  GalleryBlot.blotName = "gallery";
  GalleryBlot.tagName = "div";
  GalleryBlot.className = "article-gallery";
  Quill.register(GalleryBlot);

  class AttachmentBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      const v = typeof value === "string" ? { src: value } : (value || {});
      const name = v.name || decodeURIComponent((v.src || "").split("/").pop() || "Allegato");
      const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "file";
      node.setAttribute("href", v.src || "");
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
      node.setAttribute("data-name", name);
      node.setAttribute("data-ext", ext);
      if (v.size) node.setAttribute("data-size", String(v.size));
      node.setAttribute("contenteditable", "false");
      node.classList.add("article-attachment");
      const meta = [ext.toUpperCase(), v.size ? attFormatSize(v.size) : ""].filter(Boolean).join(" · ");
      node.innerHTML = `<span class="att-icon">${escapeHtml(ext.slice(0, 4).toUpperCase())}</span><span class="att-body"><span class="att-name">${escapeHtml(name)}</span><span class="att-meta">${escapeHtml(meta)}</span></span><span class="att-action">Apri / Scarica</span>`;
      return node;
    }
    static value(node) {
      return { src: node.getAttribute("href"), name: node.getAttribute("data-name"), size: Number(node.getAttribute("data-size")) || undefined };
    }
  }
  AttachmentBlot.blotName = "attachment";
  AttachmentBlot.tagName = "a";
  AttachmentBlot.className = "article-attachment";
  Quill.register(AttachmentBlot);

  const icons = Quill.import("ui/icons");
  icons["videofile"] = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="10" width="12" x="3" y="4" rx="1"></rect><polygon class="ql-fill" points="8 7 8 11 11 9 8 7"></polygon></svg>';
  icons["gallery"] = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="8" width="10" x="5" y="3"></rect><rect class="ql-stroke" height="8" width="10" x="3" y="7"></rect></svg>';
  icons["attachment"] = '<svg viewBox="0 0 18 18"><path class="ql-stroke" d="M12.5 6.5l-5.2 5.2a1.8 1.8 0 0 0 2.5 2.5l5.7-5.7a3.2 3.2 0 0 0-4.5-4.5L5.3 9.7a4.6 4.6 0 0 0 6.5 6.5l4-4"></path></svg>';

  Quill.__mediaBlotsRegistered = true;
}

function escapeHtml(s) { return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])); }
function attFormatSize(b) { return b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`; }

// Inserisce nel punto indicato l'embed corretto in base al tipo di file caricato
function insertUploadedAt(editor, index, res) {
  if (!editor) return;
  if (res.kind === "image") editor.insertEmbed(index, "image", res.url, "user");
  else if (res.kind === "video") editor.insertEmbed(index, "videofile", res.url, "user");
  else editor.insertEmbed(index, "attachment", { src: res.url, name: res.name, size: res.size }, "user");
  editor.setSelection(index + 1);
}

const RichTextEditor = forwardRef(function RichTextEditor({ value, onChange, placeholder = "Scrivi il contenuto dell'articolo…" }, ref) {
  const quillRef = useRef(null);
  const getEd = () => quillRef.current?.getEditor?.() || quillRef.current?.editor;
  useImperativeHandle(ref, () => ({
    insertVideoEmbed(embedUrl) { const ed = getEd(); if (!ed) return; const i = Math.max(0, ed.getLength() - 1); ed.insertEmbed(i, "video", embedUrl, "user"); ed.setSelection(i + 1); },
    insertVideoFile(url) { const ed = getEd(); if (!ed) return; const i = Math.max(0, ed.getLength() - 1); ed.insertEmbed(i, "videofile", url, "user"); ed.setSelection(i + 1); },
    insertImage(url) { const ed = getEd(); if (!ed) return; const i = Math.max(0, ed.getLength() - 1); ed.insertEmbed(i, "image", url, "user"); ed.setSelection(i + 1); },
    insertAttachment(res) { const ed = getEd(); if (!ed) return; const i = Math.max(0, ed.getLength() - 1); ed.insertEmbed(i, "attachment", { src: res.url, name: res.name, size: res.size }, "user"); ed.setSelection(i + 1); },
    insertUploaded(res) { const ed = getEd(); if (!ed) return; insertUploadedAt(ed, Math.max(0, ed.getLength() - 1), res); },
    removeNthAttachment(n) { const ed = getEd(); if (!ed) return; const node = ed.root.querySelectorAll("a.article-attachment")[n]; if (!node) return; const blot = Quill.find(node); if (blot) ed.deleteText(ed.getIndex(blot), 1, "user"); },
    getAttachments() {
      const ed = getEd(); if (!ed) return [];
      return Array.from(ed.root.querySelectorAll("a.article-attachment")).map((n) => ({ src: n.getAttribute("href") || "", name: n.getAttribute("data-name") || "", size: Number(n.getAttribute("data-size")) || 0 }));
    },
    removeNthVideo(n) { const ed = getEd(); if (!ed) return; const node = ed.root.querySelectorAll("iframe, video")[n]; if (!node) return; const blot = Quill.find(node); if (blot) ed.deleteText(ed.getIndex(blot), 1, "user"); },
    removeNthImage(n) { const ed = getEd(); if (!ed) return; const node = Array.from(ed.root.querySelectorAll("img")).filter((x) => !x.closest(".article-gallery"))[n]; if (!node) return; const blot = Quill.find(node); if (blot) ed.deleteText(ed.getIndex(blot), 1, "user"); },
    getVideos() {
      const ed = getEd(); if (!ed) return [];
      return Array.from(ed.root.querySelectorAll("iframe, video")).map((n) => {
        const src = n.getAttribute("src") || "";
        if (n.tagName.toLowerCase() === "video") return { kind: "file", src, label: "Video caricato" };
        return { kind: "embed", src, label: src.includes("vimeo") ? "Vimeo" : "YouTube" };
      });
    },
    getImages() {
      const ed = getEd(); if (!ed) return [];
      return Array.from(ed.root.querySelectorAll("img")).filter((x) => !x.closest(".article-gallery")).map((n) => ({ src: n.getAttribute("src") || "" }));
    },
    getHTML() { const ed = getEd(); return ed ? ed.root.innerHTML : null; },
  }), []);
  const [galleryDlg, setGalleryDlg] = useState({ open: false, images: [], node: null });
  const [galleryBusy, setGalleryBusy] = useState(false);

  useEffect(() => {
    __activeQuill = quillRef.current?.getEditor?.() || quillRef.current?.editor || null;
    __onGalleryEdit = (node) => {
      const imgs = node.getAttribute("data-images")
        ? node.getAttribute("data-images").split("|").filter(Boolean)
        : Array.from(node.querySelectorAll("img")).map((i) => i.getAttribute("src"));
      setGalleryDlg({ open: true, images: imgs, node });
    };
    return () => { __onGalleryEdit = null; };
  }, []);

  const moveGalleryImg = (idx, dir) => {
    setGalleryDlg((d) => {
      const arr = [...d.images];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...d, images: arr };
    });
  };
  const removeGalleryImg = (idx) => setGalleryDlg((d) => ({ ...d, images: d.images.filter((_, i) => i !== idx) }));
  const addGalleryImgs = () => {
    const input = document.createElement("input");
    input.type = "file"; input.multiple = true;
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return;
      setGalleryBusy(true);
      try {
        const urls = [];
        for (const f of files) {
          const res = await uploadAnyFile(f);
          if (res.kind === "image") urls.push(res.url);
          else { const ed = getEd(); if (ed) insertUploadedAt(ed, Math.max(0, ed.getLength() - 1), res); }
        }
        setGalleryDlg((d) => ({ ...d, images: [...d.images, ...urls] }));
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento non riuscito"); }
      finally { setGalleryBusy(false); }
    };
    input.click();
  };
  const saveGallery = () => {
    const editor = quillRef.current?.getEditor?.() || quillRef.current?.editor;
    const node = galleryDlg.node;
    if (!editor || !node) { setGalleryDlg({ open: false, images: [], node: null }); return; }
    try {
      const blot = Quill.find(node);
      const index = editor.getIndex(blot);
      editor.deleteText(index, 1, "user");
      if (galleryDlg.images.length) {
        editor.insertEmbed(index, "gallery", galleryDlg.images, "user");
        editor.setSelection(index + 1);
      }
    } catch (e) { toast.error("Impossibile aggiornare la galleria"); }
    setGalleryDlg({ open: false, images: [], node: null });
  };

  const getEditorRange = () => {
    const editor = quillRef.current?.getEditor?.() || quillRef.current?.editor;
    const range = editor?.getSelection(true) || { index: editor?.getLength() || 0 };
    return { editor, range };
  };

  // Un unico gestore per immagini, video e allegati: accetta qualsiasi file e inserisce l'embed giusto
  const makeUploadHandler = (placeholderTxt, errTxt) => () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return;
      const { editor, range } = getEditorRange();
      editor?.insertText(range.index, placeholderTxt);
      try {
        const results = [];
        for (const f of files) results.push(await uploadAnyFile(f));
        editor?.deleteText(range.index, placeholderTxt.length);
        let idx = range.index;
        for (const res of results) { insertUploadedAt(editor, idx, res); idx += 1; }
      } catch (e) {
        editor?.deleteText(range.index, placeholderTxt.length);
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || errTxt);
      }
    };
    input.click();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlers = useMemo(() => ({
    image: makeUploadHandler("⏳ caricamento immagine…", "Caricamento immagine non riuscito"),
    videofile: makeUploadHandler("⏳ caricamento video…", "Caricamento video non riuscito"),
    attachment: makeUploadHandler("⏳ caricamento file…", "Caricamento file non riuscito"),
  }), []);
  const { image: imageHandler, videofile: videoFileHandler, attachment: attachmentHandler } = handlers;

  const videoHandler = useCallback(() => {
    const raw = window.prompt("Incolla il link del video (YouTube o Vimeo). Per un file mp4 usa invece il pulsante 'Carica video'.");
    if (!raw) return;
    const url = raw.trim();
    let embed = null;
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (yt) embed = `https://www.youtube.com/embed/${yt[1]}`;
    else if (vimeo) embed = `https://player.vimeo.com/video/${vimeo[1]}`;
    if (!embed) { toast.error("Link non valido. Usa un URL di YouTube o Vimeo."); return; }
    const { editor, range } = getEditorRange();
    editor?.insertEmbed(range.index, "video", embed, "user");
    editor?.setSelection(range.index + 1);
  }, []);

  const galleryHandler = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return;
      const { editor, range } = getEditorRange();
      const placeholderTxt = `⏳ caricamento galleria (${files.length} file)…`;
      editor?.insertText(range.index, placeholderTxt);
      try {
        const urls = [];
        const others = [];
        for (const f of files) {
          const res = await uploadAnyFile(f);
          if (res.kind === "image") urls.push(res.url); else others.push(res);
        }
        editor?.deleteText(range.index, placeholderTxt.length);
        let idx = range.index;
        if (urls.length) { editor?.insertEmbed(idx, "gallery", urls, "user"); idx += 1; }
        for (const res of others) { insertUploadedAt(editor, idx, res); idx += 1; }
        editor?.setSelection(idx);
      } catch (e) {
        editor?.deleteText(range.index, placeholderTxt.length);
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento galleria non riuscito");
      }
    };
    input.click();
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image", "video", "videofile", "gallery", "attachment"],
        ["clean"],
      ],
      handlers: { image: imageHandler, video: videoHandler, videofile: videoFileHandler, gallery: galleryHandler, attachment: attachmentHandler },
    },
    blotFormatter2: {},
    clipboard: {
      matchVisual: false,
      matchers: [
        ["IFRAME", (node) => {
          const Delta = Quill.import("delta");
          const src = node.getAttribute("src") || "";
          return new Delta().insert({ video: src });
        }],
      ],
    },
  }), [imageHandler, videoHandler, videoFileHandler, galleryHandler, attachmentHandler]);

  return (
    <div data-testid="rich-text-editor" className="rte-wrapper">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        placeholder={placeholder}
      />
      <Dialog open={galleryDlg.open} onOpenChange={(v) => { if (!v) setGalleryDlg({ open: false, images: [], node: null }); }}>
        <DialogContent data-testid="gallery-manager-dialog" className="bg-[#0F172A] border-white/10 max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Gestisci galleria</DialogTitle>
            <DialogDescription>Riordina con le frecce, rimuovi o aggiungi foto. Le modifiche si applicano al salvataggio.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-2 py-1">
            {galleryDlg.images.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="gallery-empty">Nessuna foto. Aggiungine almeno una o salva per rimuovere la galleria.</p>
            ) : galleryDlg.images.map((url, i) => (
              <div key={`${url}-${i}`} data-testid={`gallery-item-${i}`} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-2">
                <img src={url} alt="" className="w-16 h-16 object-cover rounded border border-white/10" />
                <span className="text-xs text-muted-foreground flex-1 truncate">Foto {i + 1}</span>
                <button data-testid={`gallery-up-${i}`} onClick={() => moveGalleryImg(i, -1)} disabled={i === 0} className="p-1.5 rounded border border-white/15 text-muted-foreground hover:text-gold disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`gallery-down-${i}`} onClick={() => moveGalleryImg(i, 1)} disabled={i === galleryDlg.images.length - 1} className="p-1.5 rounded border border-white/15 text-muted-foreground hover:text-gold disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid={`gallery-remove-${i}`} onClick={() => removeGalleryImg(i)} className="p-1.5 rounded border border-white/15 text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <DialogFooter className="border-t border-white/10 pt-3 mt-1 gap-2">
            <Button data-testid="gallery-add" variant="outline" onClick={addGalleryImgs} disabled={galleryBusy} className="border-white/15 mr-auto">
              {galleryBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />} Aggiungi file
            </Button>
            <Button variant="outline" onClick={() => setGalleryDlg({ open: false, images: [], node: null })} className="border-white/15">Annulla</Button>
            <Button data-testid="gallery-save" onClick={saveGallery} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">Salva galleria</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default RichTextEditor;
